import {AjaxFactory} from '../../_libs/redux-observable-list/src/AjaxFactory';
import {HttpMethod} from '../../_libs/redux-observable-list/src/enums';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list';
import {makeActionCreator} from '../../core-utilities/redux';
import {ReduxRequest} from '../../core-utilities/redux-request';
import {getSuccessOnlyResponseStatus} from '../../core-utilities/redux-request/utils';
import {VideoSessionEpics} from '../epics/videoSessionEpics';
import {videoChatSessionRequestStates} from '../reducers/state';
import {logger} from './logger';
import {VIDEO_SESSION_PREFIX} from './state';


const receivedSessionToken = makeActionCreator(VIDEO_SESSION_PREFIX, 'RECEIVED_SESSION_TOKEN');
const resetSession = makeActionCreator(VIDEO_SESSION_PREFIX, 'RESET_SESSION');
const navToVideo = makeActionCreator(VIDEO_SESSION_PREFIX, 'NAV_TO_VIDEO');
const getSessionToken = makeActionCreator(VIDEO_SESSION_PREFIX, 'GET_SESSION_TOKEN');

const requests = {
  createSession: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'createSession',
    buildAjaxFactory: buildCreateSessionAjaxFactory,
  })),
  startSession: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'startSession',
    buildAjaxFactory: buildStartSessionAjaxFactory,
  })),
  joinSession: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'joinSession',
    buildAjaxFactory: buildJoinSessionAjaxFactory,
  })),
  inviteToSession: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'inviteToSession',
    buildAjaxFactory: buildInviteToSessionAjaxFactory,
  })),
  leaveSession: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'leaveSession',
    buildAjaxFactory: buildLeaveSessionAjaxFactory,
  })),
  endSession: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'endSession',
    buildAjaxFactory: buildEndSessionAjaxFactory,
  })),
  startRecording: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'startRecording',
    buildAjaxFactory: buildStartRecordingAjaxFactory,
  })),
  stopRecording: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'stopRecording',
    buildAjaxFactory: buildStopRecordingAjaxFactory,
  })),
  screenShot: new ReduxRequest(buildSessionReduxRequestOptions({
    state: 'screenShot',
    buildAjaxFactory: buildScreenShotAjaxFactory,
  })),
};

/** @type {VideoSessionActions} */
export const videoSessionsActions = {
  receivedSessionToken,
  resetSession,
  navToVideo,
  getSessionToken,
  createSession: requests.createSession.actions,
  startSession: requests.startSession.actions,
  joinSession: requests.joinSession.actions,
  inviteToSession: requests.inviteToSession.actions,
  leaveSession: requests.leaveSession.actions,
  endSession: requests.endSession.actions,
  startRecording: requests.startRecording.actions,
  stopRecording: requests.stopRecording.actions,
  screenShot: requests.screenShot.actions,
};

const reducers = buildReducers(requests);

const videoSessionEpics = new VideoSessionEpics({logger}, videoSessionsActions);

videoSessionEpics.addEpics([
  ...requests.createSession.epics.epics,
  ...requests.startSession.epics.epics,
  ...requests.joinSession.epics.epics,
  ...requests.inviteToSession.epics.epics,
  ...requests.leaveSession.epics.epics,
  ...requests.endSession.epics.epics,
  ...requests.startRecording.epics.epics,
  ...requests.stopRecording.epics.epics,
  ...requests.screenShot.epics.epics,
]);

export const videoSessionRequests = {
  /** @type {VideoSessionActions} */
  actions: videoSessionsActions,
  reducers,
  epics: videoSessionEpics,
};

function buildReducers(requests) {
  const initialState = {
    sessionToken: null,
    createSession: requests.createSession.reducers.initialState,
    startSession: requests.startSession.reducers.initialState,
    joinSession: requests.joinSession.reducers.initialState,
    inviteToSession: requests.inviteToSession.reducers.initialState,
    leaveSession: requests.leaveSession.reducers.initialState,
    endSession: requests.endSession.reducers.initialState,
    startRecording: requests.startRecording.reducers.initialState,
    stopRecording: requests.stopRecording.reducers.initialState,
    screenShot: requests.screenShot.reducers.initialState,
  };

  const createSessionReducer = requests.createSession.reducers.buildSubStateReducer('createSession');
  const startSessionReducer = requests.startSession.reducers.buildSubStateReducer('startSession');
  const joinSessionReducer = requests.joinSession.reducers.buildSubStateReducer('joinSession');
  const inviteToSessionReducer = requests.inviteToSession.reducers.buildSubStateReducer('inviteToSession');
  const leaveSessionReducer = requests.leaveSession.reducers.buildSubStateReducer('leaveSession');
  const endSessionReducer = requests.endSession.reducers.buildSubStateReducer('endSession');
  const startRecordingReducer = requests.startRecording.reducers.buildSubStateReducer('startRecording');
  const stopRecordingReducer = requests.stopRecording.reducers.buildSubStateReducer('stopRecording');
  const screenShotReducer = requests.screenShot.reducers.buildSubStateReducer('screenShot');

  const requestReducers = [
    createSessionReducer,
    startSessionReducer,
    joinSessionReducer,
    inviteToSessionReducer,
    leaveSessionReducer,
    endSessionReducer,
    startRecordingReducer,
    stopRecordingReducer,
    screenShotReducer,
  ];

  return {
    initialState,
    reducer,
  };

  function reducer(state = initialState, action) {
    switch (action.type) {
      case videoSessionsActions.resetSession.TYPE:
        return initialState;

      case videoSessionsActions.receivedSessionToken.TYPE:
        return receivedSessionTokenReducer(state, action);

      default:
        return requestReducers.reduce((prevState, reducer) => reducer(prevState, action), state);
    }
  }

  /**
   * @param state
   * @param {ReduxAction<ReceivedSessionTokenPayload>} action
   * @return
   */
  function receivedSessionTokenReducer(state, action) {
    return {
      ...state,
      sessionToken: action.payload.sessionToken,
    };
  }
}

function buildSessionReduxRequestOptions(options) {
  return {
    prefix: `${VIDEO_SESSION_PREFIX}/${options.state}`,
    getState: videoChatSessionRequestStates[options.state],
    requestFactory: options.buildAjaxFactory({
      ...BASE_REDUX_LIST_OPTIONS,
    }),
    buildRequest: (state, payload) => payload,
    getResponseStatus: getSuccessOnlyResponseStatus,
    resetActions: [resetSession],
    logger,
    ...(options.overrides || {}),
  };
}

function buildCreateSessionAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.POST,
    useBody: true,
    getBody: getInvitesBody,
    serializeBody: serializeInvitesBody,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/create`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

function buildStartSessionAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.POST,
    useBody: true,
    getBody: getInvitesBody,
    serializeBody: serializeInvitesBody,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/start`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

function buildJoinSessionAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.POST,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/join`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

function buildInviteToSessionAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.POST,
    useBody: true,
    getBody: getInvitesBody,
    serializeBody: serializeInvitesBody,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/invite`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}


// TODO: finalize
function buildLeaveSessionAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.DELETE,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/participants`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

// TODO: finalize
function buildEndSessionAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.DELETE,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/participants`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

function buildStartRecordingAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.POST,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/archive`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

function buildStopRecordingAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.DELETE,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/archive`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

// TODO: finalize
function buildScreenShotAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: HttpMethod.DELETE,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}/video-sessions/participants`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}

function getInvitesBody(request) {
  return request.invites ? {invites: request.invites} : null;
}

function serializeInvitesBody(request) {
  return request.invites ? JSON.stringify({invites: request.invites}) : null;
}

/**
 * @typedef {ReactReduxModalActions} VideoSessionActions
 * @property {ReduxActionCreator<ReceivedSessionTokenPayload>} receivedSessionToken
 * @property {ReduxActionCreator<Void>} resetSession
 * @property {ReduxActionCreator<SessionParamsPayload>} navToVideo
 * @property {ReduxActionCreator<SessionParamsPayload>} getSessionToken
 * @property {ReduxActionCreator<SessionParamsPayload>} setSessionToken
 * @property {ReduxRequestActions} createSession Create the session and get a token - done
 * @property {ReduxRequestActions} startSession Start a new seession, inviting others to join, and get a token - done
 * @property {ReduxRequestActions} joinSession Request a token - done
 * @property {ReduxRequestActions} leaveSession - probably not needed as a request, maybe just token cleanup and nav? - TODO
 * @property {ReduxRequestActions} endSession - unclear. may use mod privlages to kick everyone? - TODO
 * @property {ReduxRequestActions} startRecording Start recording session - TODO
 * @property {ReduxRequestActions} stopRecording Stop recording session - TODO
 * @property {ReduxRequestActions} screenShot Take a screen shot of the current session and save it to the list - TODO
 * TODO: associate screen shot with a requirement.
 */

/**
 * @typedef {Object} ReceivedSessionTokenPayload
 * @property {String} sessionToken
 */

/**
 * @typedef {Object} SessionParamsPayload
 * @property {String} channelId
 * @property {String} sessionId
 * @property {String} [sessionToken]
 */



// WEBPACK FOOTER //
// ./src/js/comments/redux/videoSessionRequests.js